import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  createBrowserHistory,
  RouteComponent,
  Router,
  useRouterActions,
} from 'react-resource-router';
import Popup from '@atlaskit/popup';
import { LinkItem, MenuGroup, Section } from '@atlaskit/menu';
import { Content, PageLayout, TopNavigation } from '@atlaskit/page-layout';
import {
  AtlassianNavigation,
  Help,
  ProductHome,
} from '@atlaskit/atlassian-navigation';
import { RelayEnvironmentProvider } from 'react-relay/hooks';
import RelayEnvironment from '../relay-environment';
import { AtlassianLogo } from '@atlaskit/logo';

import { homeRoute } from '~src/routes';
import { InitUFO } from '~src/utils/ufo';
import { SettingsDropdown } from './Settings';
import { ThemeContext } from './Settings/theme-context';
import {
  setGlobalTheme,
  themeObjectToString,
  type ThemeState,
  themeStringToObject,
} from '@atlaskit/tokens';

const getThemePreference = (): Partial<ThemeState> => {
  const savedThemePreference = window.localStorage.getItem('theme');
  // legacy saved theme
  if (savedThemePreference === 'light' || savedThemePreference === 'dark') {
    return { colorMode: savedThemePreference };
  } else if (savedThemePreference !== null) {
    return themeStringToObject(savedThemePreference);
  }
  return getOsThemePreference();
};

const getOsThemePreference = (): Partial<ThemeState> => {
  const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  if (darkModeMediaQuery.matches) {
    return { colorMode: 'dark' };
  }
  return { colorMode: 'light' };
};

const history = createBrowserHistory();
const appRoutes = [homeRoute];

/*
 * Components for composing top and side navigation
 */

const ProductHomeButton: FunctionComponent = () => {
  const { push } = useRouterActions();

  return (
    <ProductHome
      onClick={() => push('/')}
      icon={AtlassianLogo}
      logo={AtlassianLogo}
    />
  );
};

const HelpPopup: FunctionComponent = () => {
  const [isHelpPopupOpen, setIsHelpPopupOpen] = useState(false);

  const onClick = () => {
    setIsHelpPopupOpen(!isHelpPopupOpen);
  };

  const onClose = () => {
    setIsHelpPopupOpen(false);
  };

  // hide help menu for now as it is empty
  return (
    <div style={{ display: 'none' }}>
      <Popup
        placement="bottom-start"
        // `content` prop is miss-typed :sigh
        //
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        content={HelpPopupContent as any}
        isOpen={isHelpPopupOpen}
        onClose={onClose}
        trigger={(triggerProps) => (
          <Help
            isSelected={isHelpPopupOpen}
            onClick={onClick}
            tooltip="Help"
            {...triggerProps}
          />
        )}
      />
    </div>
  );
};

const HelpPopupContent: FunctionComponent = () => {
  return (
    <MenuGroup>
      <Section title="Feedback">
        <LinkItem href="#">{'Coming soon...'}</LinkItem>
      </Section>
    </MenuGroup>
  );
};

const TopNavigationContents: FunctionComponent = () => (
  <AtlassianNavigation
    label="site"
    moreLabel="More"
    primaryItems={[]}
    renderSettings={SettingsDropdown}
    renderProductHome={ProductHomeButton}
    renderHelp={HelpPopup}
  />
);

const AppRouteComponent: FunctionComponent = () => {
  return (
    <PageLayout>
      <TopNavigation
        isFixed={true}
        id="app-navigation"
        skipLinkTitle="Navigation"
      >
        <TopNavigationContents />
      </TopNavigation>
      <Content>
        <RouteComponent />
      </Content>
    </PageLayout>
  );
};

export const App: FunctionComponent = () => {
  const [theme, setTheme] = useState<Partial<ThemeState>>(getThemePreference());
  useEffect(() => {
    setGlobalTheme(theme);
    window.localStorage.setItem('theme', themeObjectToString(theme));
  }, [theme]);

  return (
    <>
      <InitUFO />
      <RelayEnvironmentProvider environment={RelayEnvironment}>
        <Router
          routes={appRoutes}
          history={history}
          onPrefetch={({ route }) =>
            console.log('Prefetching route', route.name)
          }
        >
          <ThemeContext.Provider value={{ theme, setTheme }}>
            <AppRouteComponent />
          </ThemeContext.Provider>
        </Router>
      </RelayEnvironmentProvider>
    </>
  );
};
